export type AppStateBackupOperation = 'export' | 'import'
export type AppStateBackupOperationToken = string

export const APP_STATE_BACKUP_BUSY_MESSAGE = 'Another backup or restore is already running.'
export const APP_STATE_BACKUP_COMPLETION_HANDOFF_KEY = 'variorum:backup-progress-completion:v1'

export type AppStateBackupProgressPhase =
  | 'waiting-for-edits'
  | 'reading-file'
  | 'collecting-workspace'
  | 'cataloguing-works'
  | 'packing-backup'
  | 'checking-backup'
  | 'protecting-current-work'
  | 'restoring-workspace'
  | 'reopening-workspace'
  | 'recovering-current-work'
  | 'ready'

export type AppStateBackupContentGroupKey =
  | 'works-and-sources'
  | 'workbench-and-alignment'
  | 'structure-and-annotations'
  | 'character-and-reader'
  | 'ledger-safe-history'
  | 'workspace-settings'
  | 'other'

interface AppStateBackupStoreGroupDefinition {
  key: Exclude<AppStateBackupContentGroupKey, 'other'>
  label: string
  description: string
  stores: readonly string[]
}

/**
 * Friendly coverage groups for every store in PRIMARY_DB_STORE_NAMES.
 * The catalog coverage test intentionally fails when a persisted store is
 * added without being assigned to one of these user-facing groups.
 */
export const APP_STATE_BACKUP_PRIMARY_STORE_GROUPS: readonly AppStateBackupStoreGroupDefinition[] = [
  {
    key: 'works-and-sources',
    label: 'Works, contexts and sources',
    description: 'Works, contexts, witnesses, source texts, manuscripts and import reviews.',
    stores: [
      'books',
      'chapters',
      'witnesses',
      'witness_text',
      'witness_segmentation',
      'source_objects',
      'source_documents',
      'section_occurrences',
      'source_coordinate_import_reviews',
      'manuscript_records',
      'authority_works',
    ],
  },
  {
    key: 'workbench-and-alignment',
    label: 'Workbench and alignment',
    description: 'Projects, assignments, alignment rows, comparisons, source maps and editing checkpoints.',
    stores: [
      'alignments',
      'segmentation_baselines',
      'edit_operations',
      'processing_checkpoints',
      'witness_chunk_results',
      'witness_comparison_layers',
      'witness_source_maps',
      'workbench_projects',
      'workbench_witness_assignments',
      'canonical_alignments',
      'anchored_rows',
      'cross_text_tradition_links',
      'translation_preview_resumes',
      'camera_lucida_inquiries',
    ],
  },
  {
    key: 'structure-and-annotations',
    label: 'Structure and annotations',
    description: 'Chapter ranges and witness scenes, structural layers, annotations and annotation runs.',
    stores: [
      'chapter_scenes',
      'witness_scenes',
      'chapter_structural_layers',
      'chapter_device_layers',
      'witness_device_layers',
      'device_annotation_runs',
    ],
  },
  {
    key: 'character-and-reader',
    label: 'Character Index and Reader research',
    description: 'Characters, linked passages, Character notes and provenance, Reader sections and research notes.',
    stores: [
      'chapter_character_layers',
      'reader_text_structure_layers',
      'annotation_research_notes',
      'analytical_task_sets',
    ],
  },
  {
    key: 'ledger-safe-history',
    label: 'Ledger Safe history',
    description: 'Ledgers, states, active pointers, snapshots and editorial events.',
    stores: [
      'ledger_safe_edit_events',
      'ledger_safe_snapshots',
      'ledger_safe_ledgers',
      'ledger_safe_ledger_states',
      'ledger_safe_active_ledgers',
      'ledger_safe_agent_change_sets',
      'ledger_safe_authority_heads',
    ],
  },
  {
    key: 'workspace-settings',
    label: 'Workspace settings',
    description: 'Saved interface preferences.',
    stores: ['ui_preferences'],
  },
] as const

export interface AppStateBackupContentGroupSummary {
  key: AppStateBackupContentGroupKey
  label: string
  description: string
  itemCount: number
  detail: string
}

export interface AppStateBackupWorkSummary {
  id: string
  title: string
  groups: AppStateBackupContentGroupSummary[]
  itemCount: number
}

export type AppStateBackupLegacyCollationStatus = 'included' | 'not-present-preserved'

export interface AppStateBackupWorkspaceInventory {
  works: AppStateBackupWorkSummary[]
  sharedGroups: AppStateBackupContentGroupSummary[]
  primaryItemCount: number
  sharedItemCount: number
  resumeItemCount: number
  browserStateItemCount: number
  legacyCollation: {
    status: AppStateBackupLegacyCollationStatus
    itemCount: number
  }
  excluded: Array<{
    id: 'matrix-sessions' | 'server-build-runs'
    label: string
    detail: string
  }>
}

export interface AppStateBackupProgressEvent {
  operation: AppStateBackupOperation
  phase: AppStateBackupProgressPhase
  percent: number | null
  message: string
  detail?: string
  inventory?: AppStateBackupWorkspaceInventory
}

export type AppStateBackupProgressListener = (event: AppStateBackupProgressEvent) => void

type Snapshot = Partial<Record<string, any[]>>

export interface BuildAppStateBackupInventoryOptions {
  resumeStores?: Snapshot
  browserStorage?: {
    local_storage?: Record<string, string>
    session_storage?: Record<string, string>
  }
  legacyCollationStores?: Snapshot
  legacyCollationStatus: AppStateBackupLegacyCollationStatus
}

interface InventoryBucket {
  groupCounts: Record<AppStateBackupContentGroupKey, number>
  storeCounts: Record<string, number>
  chapterSceneCount: number
  witnessSceneAssignmentCount: number
  characterCount: number
  characterOccurrenceCount: number
  characterNoteCount: number
  readerSectionCount: number
  structuralRangeCount: number
  researchNoteCount: number
}

const textValue = (...values: unknown[]): string => {
  const match = values.find(value => typeof value === 'string' && value.trim().length > 0)
  return typeof match === 'string' ? match.trim() : ''
}

const stringValues = (value: unknown): string[] => {
  if (typeof value === 'string' && value.trim()) return [value.trim()]
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
}

const plural = (count: number, singular: string, pluralLabel = `${singular}s`): string => (
  `${count.toLocaleString()} ${count === 1 ? singular : pluralLabel}`
)

const emptyBucket = (): InventoryBucket => ({
  groupCounts: {
    'works-and-sources': 0,
    'workbench-and-alignment': 0,
    'structure-and-annotations': 0,
    'character-and-reader': 0,
    'ledger-safe-history': 0,
    'workspace-settings': 0,
    other: 0,
  },
  storeCounts: {},
  chapterSceneCount: 0,
  witnessSceneAssignmentCount: 0,
  characterCount: 0,
  characterOccurrenceCount: 0,
  characterNoteCount: 0,
  readerSectionCount: 0,
  structuralRangeCount: 0,
  researchNoteCount: 0,
})

const groupDefinitionByStore = new Map(
  APP_STATE_BACKUP_PRIMARY_STORE_GROUPS.flatMap(group => group.stores.map(store => [store, group] as const))
)

const addRecordToBucket = (
  bucket: InventoryBucket,
  storeName: string,
  groupKey: AppStateBackupContentGroupKey,
  record: any
): void => {
  bucket.groupCounts[groupKey] += 1
  bucket.storeCounts[storeName] = (bucket.storeCounts[storeName] || 0) + 1
  if (storeName === 'chapter_scenes') {
    bucket.chapterSceneCount += Array.isArray(record?.scene_map) ? record.scene_map.length : 0
  } else if (storeName === 'witness_scenes') {
    bucket.witnessSceneAssignmentCount += Array.isArray(record?.assignments) ? record.assignments.length : 0
  } else if (storeName === 'chapter_character_layers') {
    bucket.characterCount += Array.isArray(record?.characters) ? record.characters.length : 0
    bucket.characterOccurrenceCount += Array.isArray(record?.occurrences) ? record.occurrences.length : 0
    bucket.characterNoteCount += Array.isArray(record?.notes) ? record.notes.length : 0
  } else if (storeName === 'reader_text_structure_layers') {
    bucket.readerSectionCount += Array.isArray(record?.sections) ? record.sections.length : 0
  } else if (storeName === 'chapter_structural_layers') {
    bucket.structuralRangeCount += Array.isArray(record?.ranges) ? record.ranges.length : 0
  } else if (storeName === 'annotation_research_notes') {
    bucket.researchNoteCount += 1
  }
}

const countStore = (bucket: InventoryBucket, storeName: string): number => bucket.storeCounts[storeName] || 0

const groupDetail = (key: AppStateBackupContentGroupKey, bucket: InventoryBucket): string => {
  switch (key) {
    case 'works-and-sources':
      return [
        plural(countStore(bucket, 'chapters'), 'context'),
        plural(countStore(bucket, 'witnesses'), 'witness', 'witnesses'),
        plural(countStore(bucket, 'source_objects') + countStore(bucket, 'source_documents'), 'source item'),
        plural(countStore(bucket, 'manuscript_records'), 'manuscript record'),
      ].join(' · ')
    case 'workbench-and-alignment':
      return [
        plural(countStore(bucket, 'anchored_rows'), 'aligned row'),
        plural(countStore(bucket, 'workbench_projects'), 'project'),
        plural(countStore(bucket, 'workbench_witness_assignments'), 'witness assignment'),
      ].join(' · ')
    case 'structure-and-annotations':
      return [
        plural(bucket.structuralRangeCount, 'structural range'),
        plural(bucket.chapterSceneCount, 'scene'),
        plural(bucket.witnessSceneAssignmentCount, 'witness scene assignment'),
        plural(countStore(bucket, 'chapter_device_layers') + countStore(bucket, 'witness_device_layers'), 'annotation layer'),
        plural(countStore(bucket, 'device_annotation_runs'), 'annotation run'),
      ].join(' · ')
    case 'character-and-reader':
      return [
        plural(bucket.characterCount, 'character'),
        plural(bucket.characterOccurrenceCount, 'linked passage'),
        plural(bucket.characterNoteCount, 'Character note'),
        plural(bucket.readerSectionCount, 'Reader section'),
        plural(bucket.researchNoteCount, 'research note'),
      ].join(' · ')
    case 'ledger-safe-history':
      return [
        plural(countStore(bucket, 'ledger_safe_ledgers'), 'ledger'),
        plural(countStore(bucket, 'ledger_safe_ledger_states'), 'ledger state'),
        plural(countStore(bucket, 'ledger_safe_edit_events') + countStore(bucket, 'ledger_safe_snapshots'), 'history item'),
      ].join(' · ')
    case 'workspace-settings':
      return plural(countStore(bucket, 'ui_preferences'), 'saved preference')
    case 'other':
      return 'Saved data added by a newer version of Variorum.'
  }
}

const bucketGroups = (bucket: InventoryBucket, includeEmpty: boolean): AppStateBackupContentGroupSummary[] => {
  const known = APP_STATE_BACKUP_PRIMARY_STORE_GROUPS.map(group => ({
    key: group.key,
    label: group.label,
    description: group.description,
    itemCount: bucket.groupCounts[group.key],
    detail: groupDetail(group.key, bucket),
  }))
  const other = {
    key: 'other' as const,
    label: 'Other saved workspace data',
    description: 'Saved data introduced by a newer version of Variorum.',
    itemCount: bucket.groupCounts.other,
    detail: groupDetail('other', bucket),
  }
  return [...known, other].filter(group => includeEmpty || group.itemCount > 0)
}

const bucketItemCount = (bucket: InventoryBucket): number => (
  Object.values(bucket.groupCounts).reduce((total, count) => total + count, 0)
)

export const buildAppStateBackupWorkspaceInventory = (
  snapshot: Snapshot,
  options: BuildAppStateBackupInventoryOptions
): AppStateBackupWorkspaceInventory => {
  const books = Array.isArray(snapshot.books) ? snapshot.books : []
  const chapters = Array.isArray(snapshot.chapters) ? snapshot.chapters : []
  const witnesses = Array.isArray(snapshot.witnesses) ? snapshot.witnesses : []
  const sourceObjects = Array.isArray(snapshot.source_objects) ? snapshot.source_objects : []
  const bookIds = new Set(books.map(book => textValue(book?.id)).filter(Boolean))
  const displayedBookIds = new Set<string>()
  const displayBooks = books.filter(book => {
    const id = textValue(book?.id)
    if (!id) return true
    if (displayedBookIds.has(id)) return false
    displayedBookIds.add(id)
    return true
  })
  const chapterToBook = new Map<string, string>()
  const witnessToBook = new Map<string, string>()
  const sourceToBook = new Map<string, string>()
  const authorityToBooks = new Map<string, Set<string>>()

  const connectAuthority = (authorityId: unknown, bookId: unknown): void => {
    const resolvedAuthorityId = textValue(authorityId)
    const resolvedBookId = textValue(bookId)
    if (!resolvedAuthorityId || !bookIds.has(resolvedBookId)) return
    const linkedBooks = authorityToBooks.get(resolvedAuthorityId) || new Set<string>()
    linkedBooks.add(resolvedBookId)
    authorityToBooks.set(resolvedAuthorityId, linkedBooks)
  }

  books.forEach(book => connectAuthority(book?.authority_work_id, book?.id))
  chapters.forEach(chapter => {
    const chapterId = textValue(chapter?.id)
    const bookId = textValue(chapter?.book_id)
    if (chapterId && bookIds.has(bookId)) chapterToBook.set(chapterId, bookId)
    connectAuthority(chapter?.authority_work_id, bookId)
  })
  witnesses.forEach(witness => {
    const witnessId = textValue(witness?.id)
    const directBookId = textValue(witness?.book_id)
    const bookId = bookIds.has(directBookId)
      ? directBookId
      : chapterToBook.get(textValue(witness?.chapter_id)) || ''
    if (witnessId && bookId) witnessToBook.set(witnessId, bookId)
  })
  sourceObjects.forEach(source => {
    const sourceId = textValue(source?.id)
    const bookId = textValue(source?.work_id, source?.book_id)
    if (sourceId && bookIds.has(bookId)) sourceToBook.set(sourceId, bookId)
  })

  const resolveWorkId = (storeName: string, record: any): string | null => {
    const candidates = new Set<string>()
    const addBook = (value: unknown): void => {
      stringValues(value).forEach(id => { if (bookIds.has(id)) candidates.add(id) })
    }
    const addChapter = (value: unknown): void => {
      stringValues(value).forEach(id => {
        const bookId = chapterToBook.get(id)
        if (bookId) candidates.add(bookId)
      })
    }
    const addWitness = (value: unknown): void => {
      stringValues(value).forEach(id => {
        const bookId = witnessToBook.get(id)
        if (bookId) candidates.add(bookId)
      })
    }
    const addSource = (value: unknown): void => {
      stringValues(value).forEach(id => {
        const bookId = sourceToBook.get(id)
        if (bookId) candidates.add(bookId)
      })
    }
    const addAuthority = (value: unknown): void => {
      stringValues(value).forEach(id => authorityToBooks.get(id)?.forEach(bookId => candidates.add(bookId)))
    }

    if (storeName === 'books') addBook(record?.id)
    if (storeName === 'authority_works') addAuthority(record?.id)

    ;['book_id', 'bookId', 'work_id', 'workId', 'source_book_id', 'sourceBookId'].forEach(field => addBook(record?.[field]))
    ;['book_ids', 'bookIds', 'source_book_ids', 'sourceBookIds'].forEach(field => addBook(record?.[field]))
    ;['chapter_id', 'chapterId', 'context_id', 'contextId'].forEach(field => addChapter(record?.[field]))
    ;['chapter_ids', 'chapterIds', 'context_ids', 'contextIds'].forEach(field => addChapter(record?.[field]))
    ;['witness_id', 'witnessId'].forEach(field => addWitness(record?.[field]))
    ;['witness_ids', 'witnessIds'].forEach(field => addWitness(record?.[field]))
    ;['source_object_id', 'sourceObjectId'].forEach(field => addSource(record?.[field]))
    ;['authority_work_id', 'authorityWorkId'].forEach(field => addAuthority(record?.[field]))

    if (Array.isArray(record?.build_targets)) {
      record.build_targets.forEach((target: any) => {
        addBook(target?.work_id)
        addChapter(target?.context_id)
      })
    }
    if (Array.isArray(record?.contents)) {
      record.contents.forEach((content: any) => addAuthority(content?.authority_work_id))
    }
    if (Array.isArray(record?.proposed_documents)) {
      record.proposed_documents.forEach((document: any) => {
        addBook(document?.work_id)
        addSource(document?.source_object_id)
      })
    }
    if (Array.isArray(record?.proposed_occurrences)) {
      record.proposed_occurrences.forEach((occurrence: any) => {
        addAuthority(occurrence?.authority_work_id)
        addSource(occurrence?.source_object_id)
        addWitness(occurrence?.witness_id)
      })
    }

    return candidates.size === 1 ? Array.from(candidates)[0] : null
  }

  const workBuckets = new Map<string, InventoryBucket>(books
    .map(book => textValue(book?.id))
    .filter(Boolean)
    .map(bookId => [bookId, emptyBucket()]))
  const sharedBucket = emptyBucket()

  Object.entries(snapshot).forEach(([storeName, records]) => {
    if (!Array.isArray(records)) return
    const groupKey = groupDefinitionByStore.get(storeName)?.key || 'other'
    records.forEach(record => {
      const workId = resolveWorkId(storeName, record)
      const bucket = workId ? workBuckets.get(workId) : null
      addRecordToBucket(bucket || sharedBucket, storeName, groupKey, record)
    })
  })

  const works = displayBooks.map((book, index) => {
    const id = textValue(book?.id) || `unidentified-work-${index + 1}`
    const bucket = workBuckets.get(id) || emptyBucket()
    return {
      id,
      title: textValue(book?.name, book?.title, book?.canonical_title) || `Untitled Work ${index + 1}`,
      groups: bucketGroups(bucket, true).filter(group => (
        (group.key !== 'workspace-settings' && group.key !== 'other') || group.itemCount > 0
      )),
      itemCount: bucketItemCount(bucket),
    }
  }).sort((left, right) => left.title.localeCompare(right.title))

  const countSnapshotItems = (stores?: Snapshot): number => Object.values(stores || {})
    .reduce((total, records) => total + (Array.isArray(records) ? records.length : 0), 0)
  const localStorageCount = Object.keys(options.browserStorage?.local_storage || {}).length
  const sessionStorageCount = Object.keys(options.browserStorage?.session_storage || {}).length
  const sharedItemCount = bucketItemCount(sharedBucket)

  return {
    works,
    sharedGroups: bucketGroups(sharedBucket, false),
    primaryItemCount: works.reduce((total, work) => total + work.itemCount, 0) + sharedItemCount,
    sharedItemCount,
    resumeItemCount: countSnapshotItems(options.resumeStores),
    browserStateItemCount: localStorageCount + sessionStorageCount + 1,
    legacyCollation: {
      status: options.legacyCollationStatus,
      itemCount: countSnapshotItems(options.legacyCollationStores),
    },
    excluded: [
      {
        id: 'matrix-sessions',
        label: 'Saved matrix sessions',
        detail: 'Stored on the server and require a separate server backup.',
      },
      {
        id: 'server-build-runs',
        label: 'Server Build runs',
        detail: 'Stored on the server and require a separate server backup.',
      },
    ],
  }
}

export const clampBackupProgressPercent = (percent: number | null): number | null => (
  percent === null || !Number.isFinite(percent)
    ? null
    : Math.max(0, Math.min(100, Math.round(percent)))
)
